/*
 * all this code was based from original source:
 * https://github.com/jusbrasil/react-components/blob/5899a75977c7e76cb3572b10ddbcb38a0f37d974/packages/related-doctrine/src/RelatedDoctrine.jsx
 */

import { gql } from '@apollo/client'
import { SetNonNullable } from 'type-fest'

import { ARTIFACT_LABEL } from '../../../constants'
import { UseDoctrineData_TopicFragment } from './__generated__/use-doctrine-data.graphql'

export type UseDoctrineDataProps = {
  topic: UseDoctrineData_TopicFragment | null
  title?: string
}

export type UseDoctrineDataReturn = {
  title: string
  doc: {
    docId: number
    artifact: string
    title: string
    text: string
    authors: string[]
    url: string
  }
} | null

type Topic = SetNonNullable<UseDoctrineData_TopicFragment>
type RelatedDocuments = Topic['relatedDocuments'] | Topic['lawTopic']['relatedDocuments']

const TEXT_LIMIT_SIZE = 293

export function useDoctrineData(props: UseDoctrineDataProps): UseDoctrineDataReturn {
  const { topic } = props

  if (!topic) {
    return null
  }

  const { relatedDocuments, lawTopic } = topic

  const relation =
    getFirstDoctrineRelated(relatedDocuments) || getFirstDoctrineRelated(lawTopic?.relatedDocuments)

  if (!relation) {
    return null
  }

  const { description: relationDescription, anchor, node } = relation
  const {
    artifact,
    docId,
    title,
    originalTitle,
    description: doctrineDescription,
    authors: doctrineAuthors,
    url,
    parentDocument,
  } = node || {}

  let docText = doctrineDescription
  let docUrl = url
  let docAuthors = doctrineAuthors
  let docTitle = title

  if (artifact === ARTIFACT_LABEL.SECAO_DOUTRINA) {
    docText = relationDescription
    docAuthors = doctrineAuthors?.length ? doctrineAuthors : parentDocument?.authors
    docUrl = anchor ? `${url}#${anchor}` : url
    docTitle = `${originalTitle} - ${parentDocument?.title}`
  }

  return {
    title: props.title || 'Doutrina sobre este tópico',
    doc: {
      artifact: artifact!,
      text: limitTextSize(TEXT_LIMIT_SIZE, docText!),
      title: docTitle!,
      url: docUrl!,
      docId: docId!,
      authors: (docAuthors || []).filter(Boolean) as string[],
    },
  }
}

useDoctrineData.fragments = {
  topic: gql`
    fragment UseDoctrineData_topic on Topic {
      relatedDocuments(first: 1) {
        edges {
          description
          anchor
          node {
            docId
            title
            originalTitle
            artifact
            url
            description
            authors: doctrineAuthorNames
            parentDocument {
              authors: doctrineAuthorNames
              title
            }
          }
        }
      }
      lawTopic {
        relatedDocuments(first: 1) {
          edges {
            description
            anchor
            node {
              docId
              title
              originalTitle
              artifact
              url
              description
              authors: doctrineAuthorNames
              parentDocument {
                authors: doctrineAuthorNames
                title
              }
            }
          }
        }
      }
    }
  `,
}

function getFirstDoctrineRelated(documents: RelatedDocuments | undefined) {
  if (!documents) {
    return null
  }
  const { edges } = documents
  if (!edges) {
    return null
  }
  return edges.find((relation) =>
    [ARTIFACT_LABEL.DOUTRINA, ARTIFACT_LABEL.SECAO_DOUTRINA].find(
      (i) => relation?.node?.artifact === i
    )
  )
}

function limitTextSize(size: number, fullText: string | undefined) {
  return fullText && fullText.length > size ? `${fullText.slice(0, size)}...` : fullText || ''
}
